// Cart Functions with Enhanced Error Handling and Performance

// Global cart operation state management
class CartOperationManager {
  constructor() {
    this.isOperationInProgress = false;
    this.operationType = null;
    this.operationStartTime = null;
  }

  startOperation(type) {
    if (this.isOperationInProgress) {
      throw new Error(`Cart operation already in progress: ${this.operationType}`);
    }
    this.isOperationInProgress = true;
    this.operationType = type;
    this.operationStartTime = Date.now();
    
    // Dispatch event to update UI
    document.dispatchEvent(new CustomEvent('cart-operation-start', {
      detail: { type, timestamp: this.operationStartTime }
    }));
  }

  endOperation() {
    const duration = Date.now() - this.operationStartTime;
    this.isOperationInProgress = false;
    const completedType = this.operationType;
    this.operationType = null;
    this.operationStartTime = null;
    
    // Dispatch event to update UI
    document.dispatchEvent(new CustomEvent('cart-operation-end', {
      detail: { type: completedType, duration }
    }));
  }

  isInProgress() {
    return this.isOperationInProgress;
  }

  getCurrentOperation() {
    return this.operationType;
  }
}

// Global instance
export const cartOperationManager = new CartOperationManager();

/**
 * Fetch cart data with timeout and retry logic
 */
export async function getCart(timeout = 10000) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const res = await fetch('/cart.js', {
      signal: controller.signal,
      headers: {
        'Accept': 'application/json',
        'Cache-Control': 'no-cache'
      }
    });
    
    clearTimeout(timeoutId);
    
    if (!res.ok) {
      throw new Error(`Failed to fetch cart: ${res.status} ${res.statusText}`);
    }
    
    return await res.json();
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timeout - cart fetch took too long');
    }
    throw error;
  }
}

/**
 * Add single item to cart with enhanced error handling
 */
export async function addToCart(variantId, quantity = 1, properties = {}, timeout = 10000) {
  if (!variantId) {
    throw new Error('Variant ID is required');
  }

  if (quantity < 1) {
    throw new Error('Quantity must be at least 1');
  }

  // Check if another cart operation is in progress
  if (cartOperationManager.isInProgress()) {
    throw new Error(`Cannot add to cart: ${cartOperationManager.getCurrentOperation()} operation in progress`);
  }

  cartOperationManager.startOperation('add-to-cart');

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ 
        items: [{
          id: variantId, 
          quantity, 
          properties 
        }]
      }),
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      console.error('Shopify API Error (addToCart):', {
        status: res.status,
        statusText: res.statusText,
        errorData,
        requestBody: { items: [{ id: variantId, quantity, properties }] }
      });
      throw new Error(`Failed to add to cart: ${res.status} ${res.statusText} - ${errorData.description || errorData.message || ''}`);
    }

    const result = await res.json();
    
    // Dispatch custom event with item details
    document.dispatchEvent(new CustomEvent('add-to-cart', {
      detail: {
        item: result,
        quantity,
        properties
      }
    }));

    return result;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timeout - add to cart took too long');
    }
    throw error;
  } finally {
    cartOperationManager.endOperation();
  }
}

/**
 * Add multiple items to cart with enhanced validation
 * @param {Object} params - Parameters object
 * @param {Array} params.items - Array of items to add to cart
 * @param {string|number} params.items[].id - Variant ID
 * @param {number} [params.items[].quantity=1] - Quantity to add
 * @param {Object} [params.items[].properties={}] - Item properties
 * @param {number} [timeout=15000] - Request timeout in milliseconds
 * @returns {Promise<Object>} Cart object
 */
export async function addToCartMultiple({ items }, timeout = 15000) {
  if (!Array.isArray(items) || items.length === 0) {
    throw new Error('Items array is required and cannot be empty');
  }

  // Check if another cart operation is in progress
  if (cartOperationManager.isInProgress()) {
    throw new Error(`Cannot add to cart: ${cartOperationManager.getCurrentOperation()} operation in progress`);
  }

  cartOperationManager.startOperation('add-multiple-to-cart');

  // Validate each item
  items.forEach((item, index) => {
    if (!item.id) {
      throw new Error(`Item at index ${index} is missing required 'id' field`);
    }
    if (item.quantity && item.quantity < 1) {
      throw new Error(`Item at index ${index} has invalid quantity: ${item.quantity}`);
    }
  });

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ items }),
      signal: controller.signal
    });
    
    clearTimeout(timeoutId); 
    
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      console.error('Shopify API Error:', {
        status: res.status,
        statusText: res.statusText,
        errorData,
        requestBody: { items }
      });
      throw new Error(`Failed to add multiple items to cart: ${res.status} ${res.statusText} - ${errorData.description || errorData.message || ''}`);
    }

    const result = await res.json();
    
    // Dispatch custom event with cart details
    document.dispatchEvent(new CustomEvent('add-to-cart', {
      detail: {
        cart: result,
        itemsAdded: items
      }
    }));


    document.dispatchEvent(new CustomEvent('add-to-cart-slider', {
      detail: {
        status: 'reload'
      }
    }));

    // Fetch fresh cart data and dispatch update-cart event, then start polling
    fetch('/cart.js')
      .then(res => res.json())
      .then(freshCartData => {
        document.dispatchEvent(new CustomEvent('update-cart', {
          detail: {
            cart: freshCartData
          }
        }));
        
        // Now start polling for third-party promotional additions AFTER we've updated with fresh cart data
        pollforCartChanges(freshCartData, items);
      })
      .catch(err => {
        console.warn('Failed to fetch cart for update event:', err);
        // Still try to poll with the original cart data as fallback
        pollforCartChanges(result, items);
      });

    return result;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timeout - add multiple items took too long');
    }
    throw error;
  } finally {
    cartOperationManager.endOperation();
  }
}

/**
 * Update cart item quantity with validation
 */
export async function updateCart(id, quantity, timeout = 10000) {
  if (!id) {
    throw new Error('Item ID is required');
  }

  if (quantity < 0) {
    throw new Error('Quantity cannot be negative');
  }

  // Check if another cart operation is in progress
  if (cartOperationManager.isInProgress()) {
    throw new Error(`Cannot update cart: ${cartOperationManager.getCurrentOperation()} operation in progress`);
  }

  const operationType = quantity === 0 ? 'remove-from-cart' : 'update-cart-quantity';
  cartOperationManager.startOperation(operationType);

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ 
        id, 
        quantity 
      }),
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      console.error('Shopify API Error (updateCart):', {
        status: res.status,
        statusText: res.statusText,
        errorData,
        requestBody: { id, quantity }
      });
      throw new Error(`Failed to update cart: ${res.status} ${res.statusText} - ${errorData.description || errorData.message || ''}`);
    }

    const result = await res.json();
    
    // Dispatch custom event with update details
    document.dispatchEvent(new CustomEvent('update-cart', {
      detail: {
        cart: result,
        itemId: id,
        newQuantity: quantity
      }
    }));

    // Poll for third-party changes after quantity update/removal
    // Third-party apps might remove promotional items when main products are removed
    pollforCartChangesAfterUpdate(result, operationType, id, quantity);

    return result;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timeout - update cart took too long');
    }
    throw error;
  } finally {
    cartOperationManager.endOperation();
  }
}

/**
 * Remove item from cart (sets quantity to 0)
 */
export async function removeFromCart(line, timeout = 10000) {
  if (!line) {
    throw new Error('Line item ID is required');
  }
  
  return await updateCart(line, 0, timeout);
}

/**
 * Clear entire cart
 */
export async function clearCart(timeout = 10000) {
  try {
    const cart = await getCart(timeout);
    
    if (cart.items.length === 0) {
      return cart;
    }
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      // Clear cart by setting all items to quantity 0
      const updates = cart.items.map(item => ({
        id: item.key,
        quantity: 0
      }));
            
      // Clear cart by setting all items to quantity 0 using change.js
      const clearPromises = cart.items.map(item => 
        fetch('/cart/change.js', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ 
            id: item.key, 
            quantity: 0 
          }),
          signal: controller.signal
        })
      );
      
      const results = await Promise.all(clearPromises);
      const res = results[results.length - 1]; // Use the last response
      
      clearTimeout(timeoutId);
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(`Failed to clear cart: ${res.status} ${res.statusText} - ${errorData.description || ''}`);
      }

      const result = await res.json();
      
      document.dispatchEvent(new CustomEvent('cart-cleared', {
        detail: { cart: result }
      }));

      return result;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Request timeout - clear cart took too long');
      }
      throw error;
    }
  } catch (error) {
    console.error('Failed to clear cart:', error);
    throw error;
  }
}

/**
 * Get cart attributes (notes, attributes, etc.)
 */
export async function getCartAttributes(timeout = 10000) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const res = await fetch('/cart.js', {
      signal: controller.signal,
      headers: {
        'Accept': 'application/json'
      }
    });
    
    clearTimeout(timeoutId);
    
    if (!res.ok) {
      throw new Error(`Failed to fetch cart attributes: ${res.status} ${res.statusText}`);
    }
    
    const cart = await res.json();
    return {
      note: cart.note,
      attributes: cart.attributes,
      token: cart.token
    };
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timeout - fetch cart attributes took too long');
    }
    throw error;
  }
}

/**
 * Update cart attributes (notes, attributes, etc.)
 */
export async function updateCartAttributes(attributes = {}, note = '', timeout = 10000) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ attributes, note }),
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      console.error('Shopify API Error (updateCartAttributes):', {
        status: res.status,
        statusText: res.statusText,
        errorData,
        requestBody: { attributes, note }
      });
      throw new Error(`Failed to update cart attributes: ${res.status} ${res.statusText} - ${errorData.description || errorData.message || ''}`);
    }

    const result = await res.json();
    
    document.dispatchEvent(new CustomEvent('cart-attributes-updated', {
      detail: { cart: result, attributes, note }
    }));

    return result;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timeout - update cart attributes took too long');
    }
    throw error;
  }
}

/**
 * Utility function to format currency
 */
export function formatMoney(amount, currency = 'USD', locale = 'en-US') {
  if(Shopify.scFormatMoney) {
    return Shopify.scFormatMoney(amount);
  }

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    currencyDisplay: 'narrowSymbol'
  }).format(amount / 100); // Shopify prices are in cents
}

/**
 * Utility function to validate variant ID
 */
export function isValidVariantId(id) {
  return typeof id === 'string' || typeof id === 'number';
}

/**
 * Utility function to validate quantity
 */
export function isValidQuantity(quantity) {
  return typeof quantity === 'number' && quantity >= 0 && Number.isInteger(quantity);
}

export function getSection(section) {
  let uri = window.Shopify.routes.root
  if(location.host.includes('127.0.0.1')) {
    uri = '/'
  }

  return fetch(`${uri}?section_id=${section}`)
    .then(res => res)
    .catch(err => {
      console.error(err);
    });
}

/**
 * Get recommended products
 * @param {Number} productId - Number of product ID
 * @param {String} type - Type of recommendations (complementary, related, etc.)
 * @param {Number} limit - Number of recommended products to return
 * @returns {Promise<Object>} Object with products array or empty object on error
 */
export function getRecommendedProducts(productId, type = 'complementary', limit = 4) {
  return fetch(`/recommendations/products.json?product_id=${productId}&limit=${limit}&intent=${type}`)
    .then(async res => {
      // Check if fetch actually returned a response object
      if (!res) {
        console.error(`Fetch returned null for product ${productId} - network or CORS issue`);
        return { products: [] };
      }
      
      if (!res.ok) {
        console.warn(`Failed to fetch recommendations for product ${productId}: ${res.status} ${res.statusText}`);
        return { products: [] };
      }
      
      const text = await res.text();
      if (!text || text.trim() === '') {
        console.warn(`Empty response from recommendations API for product ${productId}`);
        return { products: [] };
      }
      
      try {
        const data = JSON.parse(text);
        // Ensure we always return an object with products array
        return {
          products: Array.isArray(data.products) ? data.products : (Array.isArray(data) ? data : [])
        };
      } catch (parseError) {
        console.error(`Failed to parse recommendations response as JSON for product ${productId}:`, text);
        return { products: [] };
      }
    })
    .catch(err => {
      console.error(`Error fetching recommendations for product ${productId}:`, err);
      return { products: [] };
    });
}

/**
 * Get product
 * @param {String} productHandle - Product handle
 * @returns {Promise<Object>} Product object
 */
export function getProduct(productHandle) {  
  return fetch(`/products/${productHandle}.js`)
    .then(async res => {
      // Check if fetch actually returned a response object
      if (!res) {
        console.error(`Fetch returned null for product ${productHandle} - network or CORS issue`);
        return null;
      }
      
      if (!res.ok) {
        console.warn(`Failed to fetch product ${productHandle}: ${res.status} ${res.statusText}`);
        return null;
      }
      
      // Check if response has content before trying to parse JSON
      const text = await res.text();
      if (!text || text.trim() === '') {
        console.warn(`Empty response for product: ${productHandle}`);
        return null;
      }
      
      try {
        return JSON.parse(text);
      } catch (parseError) {
        console.error(`Failed to parse product response as JSON for ${productHandle}:`, text);
        return null;
      }
    })
    .catch(err => {
      console.error(`Error fetching product ${productHandle}:`, err);
      return null;
    });
}

/**
 * Get metafields for products
 * @param {String} type - The type of metafields to retrieve (e.g., 'product')
 * @param {Array} productIds - Array of product IDs
 * @param {Number} timeout - Request timeout in milliseconds
 * @returns {Promise<Object>} Metafields response object
 */
export async function getMetafields(type, productIds, metafieldNames, timeout = 10000) {
  if (!type) {
    throw new Error('Type is required');
  }
  
  if (!Array.isArray(productIds) || productIds.length === 0) {
    throw new Error('Product IDs array is required and cannot be empty');
  }

  if(!Array.isArray(metafieldNames)) {
    throw new Error('metafieldNames should be an array');
  }

  let baseUrl = 'custom';

  if(Shopify.shop.includes('testing')) {
    baseUrl = 'custom-dev';
  }
  
  // Convert array of IDs to comma-separated string
  // const idsString = productIds.join(',');
  
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  // const url = `https://${baseUrl}.namawell.com/metafields?type=${type}&ids=${idsString}${ metafieldName ? `&metafield_name=${metafieldName}` : ''}`
  const url = `https://${baseUrl}.namawell.com/`;

  try {
    const productFetcher = new MetadataFetch(type, productIds, url);
    await productFetcher.load();

    clearTimeout(timeoutId);

    if (!productFetcher) {
      throw new Error(`Failed to fetch metafields`);
    }

    return productFetcher
  } catch (error) {
    clearTimeout(timeoutId);
    console.error('Failed to fetch metafields:', error);
    throw error;
  }
  
  // try {
  //   const res = await fetch(url, {
  //     signal: controller.signal,
  //     headers: {
  //       'Accept': 'application/json',
  //       'Cache-Control': 'no-cache'
  //     }
  //   });
    
    
    
  //   if (!res.ok) {
  //     throw new Error(`Failed to fetch metafields: ${res.status} ${res.statusText}`);
  //   }
    
  //   // Check if response has content before trying to parse JSON
  //   const text = await res.text();
  //   if (!text || text.trim() === '') {
  //     console.warn('Empty response from metafields API');
  //     return null;
  //   }
    
  //   try {
  //     return JSON.parse(text);
  //   } catch (parseError) {
  //     console.error('Failed to parse metafields response as JSON:', text);
  //     throw new Error(`Invalid JSON response from metafields API: ${parseError.message}`);
  //   }
  // } catch (error) {
  //   clearTimeout(timeoutId);
  //   if (error.name === 'AbortError') {
  //     throw new Error('Request timeout - metafields fetch took too long');
  //   }
  //   throw error;
  // }
}

// Helper function to dispatch proper cart update events after direct cart additions
export function dispatchCartUpdateEvents(cartData, itemsAdded = []) {
  // Dispatch add-to-cart event
  document.dispatchEvent(new CustomEvent('add-to-cart', {
    detail: {
      cart: cartData,
      itemsAdded: itemsAdded
    }
  }));

  // Fetch fresh cart data and dispatch update-cart event, then start polling
  // The update-cart event will handle slider updates appropriately
  fetch('/cart.js')
    .then(res => res.json())
    .then(freshCartData => {
      document.dispatchEvent(new CustomEvent('update-cart', {
        detail: {
          cart: freshCartData
        }
      }));
      
      // Now start polling for third-party promotional additions AFTER we've updated with fresh cart data
      pollforCartChanges(freshCartData, itemsAdded);
    })
    .catch(err => {
      console.warn('Failed to fetch cart for update event:', err);
      // Still try to poll with the original cart data as fallback
      pollforCartChanges(cartData, itemsAdded);
    });

    return;
}

// Poll cart to detect third-party promotional item additions
export function pollforCartChanges(initialCartData, intentionallyAddedItems = [], maxPolls = 10, pollDelay = 500) {
  const initialItemCount = initialCartData?.item_count || 0;
  const intentionalItemsCount = Array.isArray(intentionallyAddedItems) ? intentionallyAddedItems.length : 0;
  const expectedItemCount = initialItemCount; // This should already include our intentionally added items
  
  let pollCount = 0;
    
  const intervalId = setInterval(async () => {
    pollCount++;
    
    
    try {
      const currentCartData = await getCart();
      const currentItemCount = currentCartData?.item_count || 0;
            
      // If item count has increased beyond what we expected, third-party app likely added promotional items
      if (currentItemCount > expectedItemCount) {        
        // Dispatch update-cart event with the new cart data
        document.dispatchEvent(new CustomEvent('update-cart', {
          detail: {
            cart: currentCartData,
            changedByThirdParty: true
          }
        }));
        
        // Clear polling since we detected the change
        clearInterval(intervalId);
        return;
      }
      
      // Stop polling after max attempts
      if (pollCount >= maxPolls) {        
        // Dispatch final update event with current cart state
        document.dispatchEvent(new CustomEvent('update-cart', {
          detail: {
            cart: currentCartData
          }
        }));
        
        clearInterval(intervalId);
      }
      
    } catch (error) {
      console.warn(`Failed to poll cart (attempt ${pollCount}):`, error);
      
      // Stop polling on error
      if (pollCount >= maxPolls) {
        clearInterval(intervalId);
      }
    }
    
  }, pollDelay);
}

// Poll cart after quantity updates/removals to detect third-party changes
export function pollforCartChangesAfterUpdate(initialCartData, operationType, itemId, quantity, maxPolls = 10, pollDelay = 500) {
  const initialItemCount = initialCartData?.item_count || 0;
  let pollCount = 0;
    
  const intervalId = setInterval(async () => {
    pollCount++;
    
    
    try {
      const currentCartData = await getCart();
      const currentItemCount = currentCartData?.item_count || 0;
            
      // If item count has changed beyond our expected change, third-party app likely made additional changes
      if (currentItemCount !== initialItemCount) {
        
        // Dispatch update-cart event with the new cart data
        document.dispatchEvent(new CustomEvent('update-cart', {
          detail: {
            cart: currentCartData,
            itemId: itemId,
            newQuantity: quantity,
            changedByThirdParty: true
          }
        }));
        
        // Clear polling since we detected the change
        clearInterval(intervalId);
        return;
      }
      
      // Stop polling after max attempts (shorter polling for updates)
      if (pollCount >= maxPolls) {
        clearInterval(intervalId);
      }
      
    } catch (error) {
      console.warn(`Failed to poll cart after ${operationType} (attempt ${pollCount}):`, error);
      
      // Stop polling on error
      if (pollCount >= maxPolls) {
        clearInterval(intervalId);
      }
    }
    
  }, pollDelay);
}

